import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { 
  getSavedFiltersRequest, 
  updateSavedFilterRequest,
  createSavedFilterRequest,
  deleteSavedFilterRequest,
  updatePasswordRequest,
  getCurrentSavedFilterDetailsRequest
} from "../../api";

const initialState = {
  savedFilters: [],
  getSavedFiltersInProgress: true,
  getSavedFiltersError: null, 
  updateSavedFilterInProgress: false,
  updateSavedFilterError: null,
  deleteSavedFilterInProgress: false,
  deleteSavedFilterError: null,
  createSavedFilterInProgress: false,
  createSavedFilterError: null,
  currentSavedFilter: [],
  getCurrentSavedFilterDetailsInProgress: false,
  getCurrentSavedFilterDetailsError: null,
  updatePasswordInProgress: false,
  updatePasswordError: null,
};

export const getSavedFilters = createAsyncThunk(
  "savedFilters/getSavedFilters",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSavedFiltersRequest(_);
      let { options } = response.data;
      options = options.map(option => ({
        ...option,
        label: option.name,
        value: option.id
      }));
      return options;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateSavedFilter = createAsyncThunk(
  "savedFilters/updateSavedFilter",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const {id, ...payload} = _;
      const response = await updateSavedFilterRequest(id, payload);
      dispatch(getSavedFilters());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const createSavedFilter = createAsyncThunk(
  "savedFilters/createSavedFilter",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await createSavedFilterRequest(data);
      dispatch(getSavedFilters());
      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  },
);

export const deleteSavedFilter = createAsyncThunk(
  "savedFilters/deleteSavedFilter",
  async (savedFilterId, { rejectWithValue, dispatch }) => {
    try {
      const response = await deleteSavedFilterRequest(savedFilterId);
      dispatch(getSavedFilters());
      return { savedFilterId, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getCurrentSavedFilterDetails = createAsyncThunk(
  "savedFilters/getCurrentSavedFilterDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCurrentSavedFilterDetailsRequest();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updatePassword = createAsyncThunk(
  "savedFilters/updatePassword",
  async (data, { rejectWithValue }) => {
    try {
      const response = await updatePasswordRequest(data);
      return response.data.message;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const savedFiltersSlice = createSlice({
  name: "savedFilters",
  initialState,
  reducers: {
    clearData(state) {
      state.savedFilters = [];
      state.currentSavedFilter = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSavedFilters.pending, (state) => {
        state.getSavedFiltersInProgress = true;
        state.getSavedFiltersError = null;
      })
      .addCase(getSavedFilters.fulfilled, (state, action) => {
        const payload = action.payload;
        state.savedFilters = payload;
        state.getSavedFiltersInProgress = false;
        state.getSavedFiltersError = null;
      })
      .addCase(getSavedFilters.rejected, (state, action) => {
        state.getSavedFiltersInProgress = false;
        state.getSavedFiltersError = action.payload;
      })
      .addCase(updateSavedFilter.pending, (state) => {
        state.updateSavedFilterInProgress = true;
        state.updateSavedFilterError = null;
      })
      .addCase(updateSavedFilter.fulfilled, (state, action) => {
        state.updateSavedFilterInProgress = false;
        state.updateSavedFilterError = null;
      })
      .addCase(updateSavedFilter.rejected, (state, action) => {
        state.updateSavedFilterInProgress = false;
        state.updateSavedFilterError = action.payload;
      })
      .addCase(createSavedFilter.pending, (state) => {
        state.createSavedFilterInProgress = true;
        state.createSavedFilterError = null;
      })
      .addCase(createSavedFilter.fulfilled, (state, action) => {
        state.createSavedFilterInProgress = false;
        state.createSavedFilterError = null;
      })
      .addCase(createSavedFilter.rejected, (state, action) => {
        state.createSavedFilterInProgress = false;
        state.createSavedFilterError = action.payload;
      })
      .addCase(deleteSavedFilter.pending, (state) => {
        state.deleteSavedFilterInProgress = true;
        state.deleteSavedFilterError = null;
      })
      .addCase(deleteSavedFilter.fulfilled, (state, action) => {
        const { savedFilterId } = action.payload;
        if(state.savedFilters && state.savedFilters.data) {
          state.savedFilters.data = state.savedFilters.data.filter(savedFilter => savedFilter.id !== savedFilterId);
        }
        state.deleteSavedFilterInProgress = false;
      })
      .addCase(deleteSavedFilter.rejected, (state, action) => {
        state.deleteSavedFilterInProgress = false;
        state.deleteSavedFilterError = action.payload;
      })
      .addCase(getCurrentSavedFilterDetails.pending, (state) => {
        state.getCurrentSavedFilterDetailsInProgress = true;
        state.getCurrentSavedFilterDetailsError = null;
      })
      .addCase(getCurrentSavedFilterDetails.fulfilled, (state, action) => {
        const payload = action.payload;
        const savedFilterData = payload.person || {};

        state.currentSavedFilter = { data: savedFilterData };
        state.getCurrentSavedFilterDetailsInProgress = false;
        state.getCurrentSavedFilterDetailsError = null;
      })
      .addCase(getCurrentSavedFilterDetails.rejected, (state, action) => {
        state.getCurrentSavedFilterDetailsInProgress = false;
        state.getCurrentSavedFilterDetailsError = action.payload;
      })
      .addCase(updatePassword.pending, (state) => {
        state.updatePasswordInProgress = true;
        state.updatePasswordError = null;
      })
      .addCase(updatePassword.fulfilled, (state, action) => {
        state.updatePasswordInProgress = false;
        state.updatePasswordError = null;
        state.updatePasswordMessage = action.payload;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.updatePasswordInProgress = false;
        state.updatePasswordError = action.payload;
      })
  },
});

export const { clearData } = savedFiltersSlice.actions;

export default savedFiltersSlice.reducer;
